import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { Card, Text, Spinner } from "@shopify/polaris";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import {
  useParams,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchUserDetails,
  setDestroy,
} from "containers/user/user-container.slice";
import { useAppSelector } from "hooks";
import { useAvatarChangedListener } from "features/presence/use-avatar-changed-listener";
import CabinetAssets from "features/cabinet-assets/cabinet-assets";
import CabinetFuelMonths from "features/cabinet-fuel-months/cabinet-fuel-months";
import UserBusinessTrips from "containers/user-business-trips/user-business-trips";
import UserProfileLeaves from "containers/user-profile-leaves/user-profile-leaves";
import { User } from "types/User";
import ProfileInfoTab from "containers/user-profile/profile-info-tab";
import ProfileSectionFrame from "containers/user-profile/profile-section-frame";
import ProfileDocumentsSection from "containers/user-profile/profile-documents-section";
import NewDocumentButton from "containers/user-profile/new-document-button";
import SafetySection from "containers/user-profile/safety-section";

/** What a section needs from the page around it. */
interface ProfileSectionContext {
  companyId: string;
  userId: string;
  userEntity: User;
  isGuest: boolean;
  /** The address segment that opened the section (a section can have several). */
  segment: string | undefined;
  reloadUser: () => void;
}

interface ProfileSection {
  /** The address /profile/:userId/<mainSegment> of the section. */
  mainSegment: string;
  /** More addresses that open the same section; the address stays as it is. */
  otherSegments?: string[];
  /** Addresses from before the sections; the page replaces them with the main one. */
  oldSegments?: string[];
  titleKey: string;
  hintKey: string;
  /** The main button, right of the title. */
  renderAction?: (ctx: ProfileSectionContext) => React.ReactNode;
  renderBody: (ctx: ProfileSectionContext) => React.ReactNode;
}

function ProfileHistoryLink({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) {
  const { t } = useTranslation();
  return (
    <AuditLogPopover
      companyId={companyId}
      subjectType="User"
      subjectId={userId}
      label={t("history", { defaultValue: "History" })}
    />
  );
}

// «About me» is where a bare or unknown address leads, and the only section a
// guest member has.
const INFO_SECTION: ProfileSection = {
  mainSegment: "info",
  titleKey: "cabinet_about_me",
  hintKey: "cabinet_about_me_hint",
  renderAction: ({ companyId, userId }) => (
    <ProfileHistoryLink companyId={companyId} userId={userId} />
  ),
  renderBody: (ctx) => (
    <ProfileInfoTab
      companyId={ctx.companyId}
      userId={ctx.userId}
      userEntity={ctx.userEntity}
      isGuest={ctx.isGuest}
      reloadUser={ctx.reloadUser}
    />
  ),
};

// The sections of the cabinet. How it is read: the first path segment after
// /profile/:userId is looked up in `mainSegment`, `otherSegments` and
// `oldSegments`, and the first section that lists it opens. A bare address or an
// unknown segment goes to /info, and so does a guest member for any other
// section. Every section shows its title, one line about it and its main button
// (if it has one) above its body.
const PROFILE_SECTIONS: ProfileSection[] = [
  INFO_SECTION,
  {
    // The leave request button sits in the page itself, next to the days used.
    mainSegment: "leaves",
    titleKey: "profile_leaves_tab",
    hintKey: "cabinet_leaves_hint",
    renderBody: ({ userEntity }) => (
      <UserProfileLeaves
        isOwnCabinet
        managerName={userEntity.manager?.name}
        showWallCalendar
        listInitiallyOpen
        employeeName={userEntity.name}
        employeeEmail={userEntity.email}
      />
    ),
  },
  {
    // The month table has its own main button, so the section adds none.
    mainSegment: "fuel-reports",
    oldSegments: ["expenses"],
    titleKey: "fuel_reports",
    hintKey: "cabinet_fuel_hint",
    renderBody: () => <CabinetFuelMonths />,
  },
  {
    // The list has its own main button, so the section adds none.
    mainSegment: "business-trips",
    titleKey: "business_trips",
    hintKey: "cabinet_trips_hint",
    renderBody: () => <UserBusinessTrips />,
  },
  {
    // The page has its own main button, next to its status line.
    mainSegment: "assets",
    titleKey: "cabinet_assets",
    hintKey: "cabinet_assets_hint",
    renderBody: () => <CabinetAssets />,
  },
  {
    mainSegment: "files",
    otherSegments: ["onboarding-documents", "downloads"],
    titleKey: "documents",
    hintKey: "cabinet_documents_hint",
    renderAction: ({ userId, userEntity }) => (
      <NewDocumentButton
        userId={userId}
        managerId={userEntity.manager?.id}
        labelKey="cabinet_fill_document"
      />
    ),
    renderBody: ({ companyId, userId, segment }) => (
      <ProfileDocumentsSection
        companyId={companyId}
        userId={userId}
        segment={segment}
      />
    ),
  },
  {
    mainSegment: "roles",
    otherSegments: ["medical-check"],
    titleKey: "cabinet_safety",
    hintKey: "cabinet_safety_hint",
    renderBody: ({ companyId, userId }) => <SafetySection companyId={companyId} userId={userId} />,
  },
];

/** "info" for /profile/:userId/info/..., undefined for the bare /profile/:userId. */
function profileSegment(pathname: string, userId: string | undefined) {
  if (!userId) return undefined;
  const pathAfterProfile = pathname.split(`/profile/${userId}`)[1] || "";
  return pathAfterProfile.split("/").filter(Boolean)[0];
}

/** The section an address segment opens, or undefined when no section lists it. */
function findSection(segment: string | undefined) {
  if (!segment) return undefined;
  return PROFILE_SECTIONS.find((section) =>
    [
      section.mainSegment,
      ...(section.otherSegments ?? []),
      ...(section.oldSegments ?? []),
    ].includes(segment)
  );
}

/** Keeps profile data in Redux across section route remounts (same company + user). */
let profileLoadedScope: string | null = null;

function UserContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { userId, companyId } = useParams();
  const { session, role } = useAppSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const location = useLocation();

  const { userEntity, isLoading } = useSelector(
    (state: RootState) => state.userContainerReducer
  );

  const isSelfProfile =
    session?.id != null &&
    userId != null &&
    String(session.id) === String(userId);
  const isGuest = isSelfProfile
    ? userEntity
      ? userEntity.isUserGuest === true
      : role?.isUserGuest === true
    : false;

  const segment = profileSegment(location.pathname, userId);
  const addressedSection = findSection(segment);
  const shownSection = isGuest ? INFO_SECTION : addressedSection ?? INFO_SECTION;

  useEffect(() => {
    if (!companyId || !userId || !isSelfProfile) return;
    const profileUrl = `/app/${companyId}/profile/${userId}`;
    const openSegment = (target: string) =>
      navigate(`${profileUrl}/${target}`, { replace: true });

    if (isGuest) {
      if (segment !== INFO_SECTION.mainSegment) {
        openSegment(INFO_SECTION.mainSegment);
      }
      return;
    }
    if (!addressedSection) {
      openSegment(INFO_SECTION.mainSegment);
      return;
    }
    if (segment && addressedSection.oldSegments?.includes(segment)) {
      openSegment(addressedSection.mainSegment);
    }
  }, [
    companyId,
    userId,
    isSelfProfile,
    isGuest,
    segment,
    addressedSection,
    navigate,
  ]);

  const loadUserData = useCallback(() => {
    if (!userId || !companyId) return;
    dispatch(fetchUserDetails({ userId, companyId }));
  }, [userId, companyId, dispatch]);

  useEffect(() => {
    if (!userId || !companyId) return;

    const scope = `${companyId}:${userId}`;
    if (
      profileLoadedScope === scope &&
      userEntity &&
      String(userEntity.id) === String(userId)
    ) {
      return;
    }

    profileLoadedScope = scope;
    if (userEntity && String(userEntity.id) !== String(userId)) {
      dispatch(setDestroy());
    }
    dispatch(fetchUserDetails({ userId, companyId }));
  }, [userId, companyId, userEntity?.id, dispatch]);

  // Real-time: another tab/admin updated the displayed user's avatar — refresh.
  useAvatarChangedListener(userId, loadUserData);

  if (!isSelfProfile) {
    return <Navigate to={`/app/${companyId}`} replace />;
  }

  if (!userEntity) {
    if (isLoading) {
      return (
        <div className="px-4 py-6 lg:px-8">
          <Card roundedAbove="sm">
            <div className="flex flex-col items-center justify-center gap-4 py-16">
              <Spinner size="large" accessibilityLabel={t("loading")} />
              <Text as="p" variant="bodySm" tone="subdued">
                {t("loading", { defaultValue: "Loading" })}
              </Text>
            </div>
          </Card>
        </div>
      );
    }
    return null;
  }

  const context: ProfileSectionContext = {
    companyId: companyId!,
    userId: userId!,
    userEntity,
    isGuest,
    segment,
    reloadUser: loadUserData,
  };

  return (
    <ProfileSectionFrame
      title={t(shownSection.titleKey)}
      hint={t(shownSection.hintKey)}
      action={shownSection.renderAction?.(context)}
    >
      {shownSection.renderBody(context)}
    </ProfileSectionFrame>
  );
}

export default UserContainer;
